/**
 * Profile Curve Contract — typed tables + curve construction.
 *
 * Card: P0-GEOM (encode resolved geometry answers) · feeds P2 RenderFacet + P3 spec sheet.
 * Status: v1.0 — every input verified from source, nothing invented:
 *
 *   - Camber mm heights: Pete Wagner, Slack 2026-09-25 ("Need 2 geometry numbers…").
 *   - Rocker name semantics `<run_cm>-<2×rise_mm>`: Pete Wagner, Slack 2026-09-25
 *     (run = cm the upturn starts closer to the bindings from the end's widest
 *     point; second number is twice the rise in mm at that widest point).
 *   - Tip/tail block arcs + formula: Ullr (calc/src/orderformcalc.ts —
 *     height = R − cos(lengthToContact / R)·R, R = noseBlock.upturnRadius),
 *     verified against production reference data
 *     (docs/reference/fixtures/ullr-reference-blocks-rockers-20260925.json).
 *
 * Nothing here re-implements the calc engine: block arcs stay in calc (the
 * single source of truth); this module types the reference tables, decodes
 * rocker names, and assembles the customer-facing profile curve from the
 * engine's block-arc outputs plus the run/rise table.
 */

// ---------------------------------------------------------------------------
// Camber (OrderForm.camber is the string enum '-1'..'3' in production)
// ---------------------------------------------------------------------------

export const CAMBER_HEIGHTS_MM = {
  '-1': -5, // Reverse — reverse camber, center at −5mm
  '0': 0,   // None — flat
  '1': 3,   // Low
  '2': 5,   // Medium
  '3': 8,   // High
} as const;

export type CamberEnum = keyof typeof CAMBER_HEIGHTS_MM;

export interface CamberSpec {
  /** Raw OrderForm.camber string ('-1'..'3'). */
  value: CamberEnum;
  name: 'Reverse' | 'None' | 'Low' | 'Medium' | 'High';
  /** Signed height at the center of the camber, mm. Verified: Pete, 2026-09-25. */
  heightMm: number;
}

export function camberSpec(value: string): CamberSpec | null {
  if (!(value in CAMBER_HEIGHTS_MM)) return null;
  const v = value as CamberEnum;
  const names: Record<CamberEnum, CamberSpec['name']> = {
    '-1': 'Reverse', '0': 'None', '1': 'Low', '2': 'Medium', '3': 'High',
  };
  return { value: v, name: names[v], heightMm: CAMBER_HEIGHTS_MM[v] };
}

// ---------------------------------------------------------------------------
// Rocker names — format `<run_cm>-<2×rise_mm>` (Pete, 2026-09-25)
// ---------------------------------------------------------------------------

/** Production TipRocker/TailRocker rows are exactly these names. */
export const ROCKER_NAMES = [
  '5-5', '10-10', '12-10', '15-10', '20-10', '30-10', '40-10',
] as const;

export type RockerName = (typeof ROCKER_NAMES)[number];

export interface RockerSpec {
  name: RockerName;
  /** Run in cm: the upturn starts this many cm closer to the bindings from the end's widest point. */
  runCm: number;
  /** Rise in mm at the end's widest point = second number ÷ 2. */
  riseMm: number;
}

/**
 * Decode a rocker name. Returns null for names outside the production set —
 * callers must treat that as an explicit unknown, never guess a fallback.
 */
export function rockerSpec(name: string): RockerSpec | null {
  if (!(ROCKER_NAMES as readonly string[]).includes(name)) return null;
  const [runStr, twiceRiseStr] = name.split('-');
  return {
    name: name as RockerName,
    runCm: Number(runStr),
    riseMm: Number(twiceRiseStr) / 2,
  };
}

// ---------------------------------------------------------------------------
// Profile curve assembly
// ---------------------------------------------------------------------------

/** One engine-computed end arc (from calc `orderFormCalc.noseHeight`/`tailHeight`). */
export interface EndArc {
  /** Signed rise of the arc at the ski's end, mm (estimatedHeightM). */
  heightMm: number;
  /** Length from the end to the contact point, mm (lengthToContact). */
  lengthToContactMm: number;
  /** Block upturn radius, mm — provenance for the arc's shape. */
  upturnRadiusMm: number;
}

export interface ProfileCurveContract {
  contractVersion: '1.0';
  /** Total ski length, mm (OrderForm.length). */
  lengthMm: number;
  camber: CamberSpec;
  /** Signed center height, mm (negative = reverse). 0 = flat. */
  camberHeightMm: number;
  /** Contact-point to contact-point span the camber bow spans, mm. */
  runningLengthMm: number;
  nose: { arc: EndArc; rocker: RockerSpec | null };
  tail: { arc: EndArc; rocker: RockerSpec | null };
}

/**
 * Assemble the profile-curve contract for a design. Pure: no I/O, no
 * re-derivation of the block arcs — the engine owns those.
 */
export function buildProfileCurve(input: {
  lengthMm: number;
  runningLengthMm: number;
  camber: string;
  noseArc: EndArc;
  tailArc: EndArc;
  tipRockerName?: string | null;
  tailRockerName?: string | null;
}): ProfileCurveContract | null {
  const camber = camberSpec(input.camber);
  if (!camber) return null; // unknown camber value → explicit unknown
  if (input.runningLengthMm <= 0 || input.lengthMm <= 0) return null;
  const noseRocker = input.tipRockerName ? rockerSpec(input.tipRockerName) : null;
  const tailRocker = input.tailRockerName ? rockerSpec(input.tailRockerName) : null;
  return {
    contractVersion: '1.0',
    lengthMm: input.lengthMm,
    camber,
    camberHeightMm: camber.heightMm,
    runningLengthMm: input.runningLengthMm,
    nose: { arc: input.noseArc, rocker: noseRocker },
    tail: { arc: input.tailArc, rocker: tailRocker },
  };
}

/**
 * Sample the side-profile polyline for drawing (mm, ski axis from tail end
 * x=0 to nose end x=lengthMm, height up from a flat baseline).
 *
 * Shape rules (all sourced):
 *   - Camber bow: circular arc of signed sagitta `camberHeightMm` spanning
 *     the running length between contact points (Pete's mm table).
 *   - Tip/tail: circular arcs of the block's upturn radius, as computed by
 *     the engine (EndArc); the rocker run/rise table labels the drawing and
 *     cross-checks the visual, it does not replace the block arcs.
 */
export function sampleProfileCurve(
  c: ProfileCurveContract,
  stepMm = 5,
): Array<{ xMm: number; heightMm: number }> {
  const L = c.lengthMm;
  const noseContact = L - c.nose.arc.lengthToContactMm;
  const tailContact = c.tail.arc.lengthToContactMm;
  const pts: Array<{ xMm: number; heightMm: number }> = [];

  // Tail arc (x: 0 → tailContact): engine block arc, sampled from the
  // circular-arc formula height = R − cos(len/R)·R anchored at the contact.
  const tR = c.tail.arc.upturnRadiusMm;
  for (let x = 0; x <= tailContact + 1e-9; x += stepMm) {
    const s = tailContact - x; // distance back from contact toward tail end
    pts.push({ xMm: x, heightMm: tR - Math.cos(s / tR) * tR });
  }

  // Camber bow (tailContact → noseContact): circular arc, signed sagitta.
  const span = noseContact - tailContact;
  if (span > 0) {
    const h = c.camberHeightMm;
    const r = (span * span) / (8 * Math.abs(h || 1e-9)) + Math.abs(h || 1e-9) / 2;
    const xc = (tailContact + noseContact) / 2;
    for (let x = tailContact + stepMm; x < noseContact - stepMm / 2; x += stepMm) {
      const dx = Math.abs(x - xc);
      // Height of the circular arc with signed sagitta h over half-span span/2,
      // mirrored below the baseline for reverse camber:
      //   y = ±(sqrt(r² − dx²) − (r − |h|))
      const yy = h >= 0
        ? Math.sqrt(Math.max(r * r - dx * dx, 0)) - (r - h)
        : -(Math.sqrt(Math.max(r * r - dx * dx, 0)) - (r + h));
      pts.push({ xMm: x, heightMm: yy });
    }
  }

  // Nose arc (noseContact → L): mirror of tail arc at the other end.
  const nR = c.nose.arc.upturnRadiusMm;
  for (let x = Math.max(noseContact, tailContact + stepMm); x <= L + 1e-9; x += stepMm) {
    const s = x - noseContact;
    pts.push({ xMm: x, heightMm: nR - Math.cos(s / nR) * nR });
  }
  if (pts.length && Math.abs(pts[pts.length - 1].xMm - L) > 1e-9) {
    const s = L - noseContact;
    pts.push({ xMm: L, heightMm: nR - Math.cos(s / nR) * nR });
  }
  return pts;
}
